import { useRef, useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface MermaidScrollGuardProps {
  children: ReactNode;
  className?: string;
}

const isMac =
  typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac');

/**
 * Prevents accidental scroll-zoom on Streamdown Mermaid diagrams.
 *
 * Root cause: streamdown's internal diagram viewer registers a {passive:false}
 * wheel listener on every mermaid container and unconditionally calls
 * e.preventDefault() — this swallows all vertical scroll events over the
 * diagram and applies the delta as a zoom instead.
 *
 * Fix: a {capture:true, passive:true} wheel listener fires before streamdown's
 * bubble-phase handler. When the user scrolls without holding Ctrl/⌘,
 * stopPropagation() is called so streamdown's handler never runs; the browser
 * still scrolls the page because preventDefault() was never called.
 * When Ctrl/⌘ is held the event passes through for intentional diagram zoom.
 *
 * macOS trackpad pinch gestures arrive as wheel events with ctrlKey:true
 * (the OS maps pinch → Ctrl+wheel), so pinch-to-zoom works automatically.
 */
export function MermaidScrollGuard({ children, className }: MermaidScrollGuardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        // Stop the event before streamdown's zoom handler sees it.
        // Not calling preventDefault() lets the browser scroll the nearest
        // scrollable ancestor (chat panel, page, etc.) as normal.
        e.stopPropagation();
      }
      // Ctrl/⌘ + scroll (and macOS pinch) passes through to streamdown,
      // which zooms the diagram and calls preventDefault() to block page scroll.
    };

    // capture: true  — fires in the capture phase, before streamdown's
    //                  bubble-phase listener on the inner diagram div.
    // passive: true  — the browser doesn't need to wait for our handler before
    //                  initiating scroll (no performance penalty).
    el.addEventListener('wheel', onWheel, { capture: true, passive: true });
    return () => el.removeEventListener('wheel', onWheel, { capture: true });
  }, []);

  return (
    <div
      ref={ref}
      className={cn('group', className)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <p
        className={cn(
          'mt-0.5 text-center text-[9px] text-muted-foreground/50 select-none transition-opacity duration-200',
          hovered ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden="true"
      >
        {isMac ? '⌘' : 'Ctrl'} + scroll to zoom diagrams · drag to pan
      </p>
    </div>
  );
}
